import { addressConfig } from '@/config/address';
import { useAddressMock } from '@/composables/useAddressMock';
import type { Address as IAddress, AddressFormData, AddressResponse } from '@/types/Address';

class AddressService {
  private mockService = useAddressMock();

  // Get all addresses for the current user
  async getAddresses() {
    if (addressConfig.useMockData) {
      return this.mockService.getAddresses();
    }
    
    try {
      const { $api } = useNuxtApp();
      const response = await $api<AddressResponse>(addressConfig.endpoints.addresses, {
        method: 'GET',
      });
      
      if (response.success && Array.isArray(response.data)) {
        // Ensure we return plain objects to avoid serialization issues
        const addresses = JSON.parse(JSON.stringify(response.data));
        return {
          data: ref(addresses),
          pending: ref(false),
          error: ref(null),
        };
      }
      
      throw createError({
        statusCode: response.http_status || 500,
        statusMessage: response.message || 'Failed to fetch addresses'
      });
    } catch (error) {
      console.error('API error:', error);
      return {
        data: ref([]),
        pending: ref(false),
        error: ref(error),
      };
    }
  }

  // Get the default address
  async getDefaultAddress() {
    if (addressConfig.useMockData) {
      return this.mockService.getDefaultAddress();
    }
    
    try {
      const { data } = await this.getAddresses();
      const addresses = data.value || [];
      const defaultAddress = addresses.find(addr => addr.is_default);
      
      return {
        data: ref(defaultAddress || null),
        pending: ref(false),
        error: ref(null),
      };
    } catch (error) {
      return {
        data: ref(null),
        pending: ref(false),
        error: ref(error),
      };
    }
  }

  // Get a specific address by ID
  async getAddress(id: number) {
    if (addressConfig.useMockData) {
      const addresses = this.mockService.addresses.value;
      const address = addresses.find(addr => addr.id === id);
      return Promise.resolve({
        data: ref(address || null),
      });
    }
    
    try {
      const { $api } = useNuxtApp();
      const response = await $api<AddressResponse>(addressConfig.endpoints.getAddress(id), {
        method: 'GET',
      });
      
      if (response.success && response.data && !Array.isArray(response.data)) {
        return {
          data: ref(response.data),
          error: ref(null),
        };
      }
      
      throw createError({
        statusCode: response.http_status || 500,
        statusMessage: response.message || 'Failed to fetch address'
      });
    } catch (error) {
      return {
        data: ref(null),
        error: ref(error),
      };
    }
  }

  // Create a new address
  async createAddress(addressData: AddressFormData) {
    if (addressConfig.useMockData) {
      return this.mockService.createAddress(addressData);
    }
    
    try {
      const { $api } = useNuxtApp();
      console.log('Creating address with data:', addressData);
      
      // Use the new API payload format
      const payload = {
        place_id: addressData.place_id,
        full_name: addressData.full_name,
        address_line_1: addressData.address_line_1,
        types: addressData.types || [],
        ...(addressData.address_nickname && { address_nickname: addressData.address_nickname }),
        ...(addressData.is_default && { is_default: addressData.is_default }),
      };
      
      const response = await $api<AddressResponse>(addressConfig.endpoints.createAddress, {
        method: 'POST',
        body: payload,
      });

      console.log('Create address response:', response);

      if (response.success && response.data && !Array.isArray(response.data)) {
        return {
          data: ref(response.data),
          error: ref(null),
        };
      }
      
      throw createError({
        statusCode: response.http_status || 500,
        statusMessage: response.message || 'Failed to create address'
      });
    } catch (error: any) {
      console.error('Create address error:', error);
      return {
        data: ref(null),
        error: ref(error.message || 'Failed to create address'),
      };
    }
  }

  // Update an existing address
  async updateAddress(id: number, addressData: Partial<AddressFormData>) {
    if (addressConfig.useMockData) {
      return this.mockService.updateAddress(id, addressData);
    }
    
    try {
      const { $api } = useNuxtApp();
      console.log('Updating address:', id, addressData);
      
      // Use the new API payload format
      const payload = {
        place_id: addressData.place_id || '',
        full_name: addressData.full_name || '',
        address_line_1: addressData.address_line_1 || '',
        types: addressData.types || [],
        address_nickname: addressData.address_nickname || '',
        is_default: addressData.is_default || false,
      };
      
      const response = await $api<AddressResponse>(addressConfig.endpoints.updateAddress(id), {
        method: 'PUT',
        body: payload,
      });

      if (response.success && response.data && !Array.isArray(response.data)) {
        return {
          data: ref(response.data),
          error: ref(null),
        };
      }
      
      throw createError({
        statusCode: response.http_status || 500,
        statusMessage: response.message || 'Failed to update address'
      });
    } catch (error: any) {
      return {
        data: ref(null),
        error: ref(error.message || 'Failed to update address'),
      };
    }
  }

  // Delete an address
  async deleteAddress(id: number) {
    if (addressConfig.useMockData) {
      return this.mockService.deleteAddress(id);
    }
    
    try {
      const { $api } = useNuxtApp();
      const response = await $api<AddressResponse>(addressConfig.endpoints.deleteAddress(id), {
        method: 'DELETE',
      });

      if (response.success) {
        return {
          data: ref(true),
          error: ref(null),
        };
      }
      
      throw createError({
        statusCode: response.http_status || 500,
        statusMessage: response.message || 'Failed to delete address'
      });
    } catch (error: any) {
      return {
        data: ref(false),
        error: ref(error.message || 'Failed to delete address'),
      };
    }
  }

  // Set an address as default
  async setDefaultAddress(id: number) {
    if (addressConfig.useMockData) {
      return this.mockService.setDefaultAddress(id);
    }
    
    try {
      const { $api } = useNuxtApp();
      console.log('Setting address as default:', id);
      const response = await $api<AddressResponse>(addressConfig.endpoints.setDefaultAddress(id), {
        method: 'POST',
      });

      console.log('Set default response:', response);

      if (response.success && response.data && !Array.isArray(response.data)) {
        return {
          data: ref(response.data),
          error: ref(null),
        };
      }
      
      throw createError({
        statusCode: response.http_status || 500,
        statusMessage: response.message || 'Failed to set default address'
      });
    } catch (error: any) {
      console.error('Set default address error:', error);
      return {
        data: ref(null),
        error: ref(error.message || 'Failed to set default address'),
      };
    }
  }

  // Search addresses (for location dropdown)
  async searchAddresses(query: string) {
    if (addressConfig.useMockData) {
      const addresses = this.mockService.addresses.value;
      const filtered = addresses.filter(addr => 
        addr.address_line_1.toLowerCase().includes(query.toLowerCase()) ||
        addr.city.toLowerCase().includes(query.toLowerCase()) ||
        addr.state_province_region.toLowerCase().includes(query.toLowerCase()) ||
        (addr.address_nickname && addr.address_nickname.toLowerCase().includes(query.toLowerCase()))
      );
      return Promise.resolve({
        data: ref(filtered),
      });
    }
    
    // For real API, we'll just return all addresses and filter client-side
    // You can modify this if your API supports search parameters
    const { data } = await this.getAddresses();
    const addresses = data.value || [];
    const filtered = addresses.filter(addr => 
      addr.address_line_1.toLowerCase().includes(query.toLowerCase()) ||
      addr.city.toLowerCase().includes(query.toLowerCase()) ||
      addr.state_province_region.toLowerCase().includes(query.toLowerCase()) ||
      (addr.address_nickname && addr.address_nickname.toLowerCase().includes(query.toLowerCase()))
    );
    
    return {
      data: ref(filtered),
    };
  }
}

const addressService = new AddressService();
export default addressService; 